import React from 'react';
import {
  Box,
  Card,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

interface PromptsSidebarProps {
  open: boolean;
  onClose: () => void;
  onSelectPrompt: (prompt: string) => void;
}

const PromptsSidebar: React.FC<PromptsSidebarProps> = ({ open, onClose, onSelectPrompt }) => {
  const theme = useTheme();

  const promptTemplates = [
    { 
      label: '✨ Simplify content', 
      prompt: 'Simplify the content and make it easier to understand',
      category: 'Tone',
    },
    { 
      label: '🎓 Make more advanced', 
      prompt: 'Make the content more advanced and technical',
      category: 'Tone',
    },
    { 
      label: '📝 Improve grammar', 
      prompt: 'Improve grammar and fix any errors',
      category: 'Quality',
    },
    { 
      label: '🎯 Add more details', 
      prompt: 'Add more details and explanations',
      category: 'Content',
    },
    { 
      label: '✂️ Make it concise', 
      prompt: 'Make the content more concise and to the point',
      category: 'Length',
    },
    { 
      label: '💡 Suggest improvements', 
      prompt: 'Suggest improvements to the overall structure and content',
      category: 'Quality',
    },
    { 
      label: '📄 Production-ready (Patient Report)', 
      prompt: 'Adjust the tone to a finalized institutional clinical report in first-person plural, preserving the original content, structure, and section order without modification, eliminating third-person or AI-related language.',
      category: 'Format',
    },
     { 
      label: '📄 Advisory Note', 
      prompt: 'Include an appropriate medical advisory as a note stating that this report is for informational purposes only and does not substitute a comprehensive in-person medical examination by a qualified healthcare professional.',
      category: 'Format',
    },
    { 
      label: '📋 Add section headers', 
      prompt: 'Add clear section headers and organize the content better',
      category: 'Structure',
    },
    { 
      label: '🔬 Add scientific references-{BETA, review before you send}', 
      prompt: 'Add scientific justification and references where appropriate',
      category: 'Content',
    },
    { 
      label: '👥 Patient-friendly language', 
      prompt: 'Rewrite using patient-friendly language that is easy to understand',
      category: 'Tone',
    },
    { 
      label: '⚡ Quick summary', 
      prompt: 'Create a brief summary of the key points',
      category: 'Length',
    },
    { 
      label: '🎨 Improve formatting', 
      prompt: 'Improve the formatting, bullet points, and visual structure',
      category: 'Format',
    },
  ];

  const handlePromptClick = (prompt: string) => {
    onSelectPrompt(prompt);
    onClose();
  };

  const categories = Array.from(new Set(promptTemplates.map(p => p.category)));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="finalization-drawer"
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '450px', md: '500px' },
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(to bottom, #0d0d0f 0%, #1a1a1e 100%)'
            : 'linear-gradient(to bottom, #ffffff 0%, #f5f7fa 100%)',
          borderLeft: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === 'dark'
              ? 'rgba(255,214,10,0.1)'
              : 'rgba(255,214,10,0.05)',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #FFD60A 0%, #FFB800 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(255,214,10,0.3)',
                }}
              >
                <LightbulbIcon sx={{ color: '#000', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Finalization Templates
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Quick templates to finalize your report
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <Stack spacing={3}>
            {categories.map((category) => (
              <Box key={category}>
                <Chip
                  label={category}
                  size="small"
                  sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    fontSize: '11px',
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255,214,10,0.15)'
                      : 'rgba(255,214,10,0.1)',
                    color: '#FFD60A',
                    borderRadius: 2,
                  }}
                />
                <Stack spacing={1.5}>
                  {promptTemplates
                    .filter((template) => template.category === category)
                    .map((template, index) => (
                      <Card
                        key={index}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(0,0,0,0.02)',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 3,
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(255,214,10,0.2)',
                            borderColor: '#FFD60A',
                            background: theme.palette.mode === 'dark'
                              ? 'rgba(255,214,10,0.1)'
                              : 'rgba(255,214,10,0.05)',
                          },
                        }}
                        onClick={() => handlePromptClick(template.prompt)}
                      >
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            mb: 0.5,
                          }}
                        >
                          {template.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '12px',
                            display: 'block',
                            fontStyle: 'italic',
                          }}
                        >
                          "{template.prompt}"
                        </Typography>
                      </Card>
                    ))}
                </Stack>
              </Box>
            ))}
          </Stack>

          {/* Footer tip */}
          <Box
            sx={{
              mt: 4,
              p: 2,
              borderRadius: 3,
              background: theme.palette.mode === 'dark'
                ? 'rgba(255,214,10,0.08)'
                : 'rgba(255,214,10,0.05)',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '12px',
                display: 'block',
                lineHeight: 1.6,
              }}
            >
              💡 <strong>Tip:</strong> Click any button to fill the chat input. You can
               combine templates or edit them before sending.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default PromptsSidebar;
